const normaliseEnrolleeNumber = (encounter) => String(encounter?.enrollee_number || "").trim().toLowerCase();

const encounterDateKey = (encounter) => {
  const rawDate = encounter?.encounter_date || encounter?.encounter_start_date || encounter?.start_date;
  return rawDate ? String(rawDate).split("T")[0].split(" ")[0] : "";
};

const formatMarkerDate = (value) => {
  if (!value) {
    return "unknown date";
  }

  const date = new Date(String(value).replace(" ", "T"));

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(undefined, { month: "short", day: "numeric" });
};

const rawSentTotal = (encounter) => {
  const claims = encounter?.claims || [];

  if (claims.length) {
    return claims.reduce((total, claim) => total + Number(claim?.sent_price || 0), 0);
  }

  return Number(encounter?.sent || 0);
};

const formatMarkerAmount = (value) => {
  return `\u20a6${Number(value || 0)
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const buildMarkers = (encounter, duplicateKeys, multipleVisitKeys, duplicateGroups, multipleVisitGroups) => {
  const enrolleeKey = normaliseEnrolleeNumber(encounter);
  const dateKey = encounterDateKey(encounter);
  const duplicateGroup = duplicateGroups.get(`${enrolleeKey}|${dateKey}`) || [];
  const visitGroup = multipleVisitGroups.get(enrolleeKey) || [];
  const otherDuplicates = duplicateGroup.filter((item) => Number(item.id) !== Number(encounter?.id));
  const otherVisits = visitGroup.filter((item) => Number(item.id) !== Number(encounter?.id));
  const markers = [];

  if (enrolleeKey && dateKey && duplicateKeys.has(`${enrolleeKey}|${dateKey}`)) {
    markers.push({
      type: "duplicate",
      icon: "content_copy",
      label: "Duplicate",
      title: "Same enrollee and encounter date appears more than once in this claim batch.",
      detail: otherDuplicates.length
        ? `Possible duplicate: ${otherDuplicates.length} other bill${otherDuplicates.length === 1 ? "" : "s"} for this enrollee use ${formatMarkerDate(encounter?.encounter_date)} in this batch. Their sent amount${otherDuplicates.length === 1 ? " is" : "s are"} ${otherDuplicates.map((item) => formatMarkerAmount(rawSentTotal(item))).join(", ")}.`
        : "Possible duplicate: another bill for this enrollee uses the same encounter date in this batch.",
    });
  }

  if (enrolleeKey && multipleVisitKeys.has(enrolleeKey)) {
    markers.push({
      type: "multiple",
      icon: "repeat",
      label: "Multiple Visit",
      title: "This enrollee has visits on different dates in this claim batch.",
      detail: otherVisits.length
        ? `This enrollee has ${visitGroup.length} visits this month. ${otherVisits.length === 1 ? "Other visit" : "Other visits"}: ${otherVisits.map((item) => `${formatMarkerDate(item.encounter_date)} (submitted: ${formatMarkerAmount(rawSentTotal(item))})`).join("; ")}.`
        : "This enrollee has another visit on a different date in this batch.",
    });
  }

  return markers;
};

export const decoratedClaimEncounters = (encounters = []) => {
  const sortedEncounters = [...encounters].sort((left, right) => {
    const byEnrollee = normaliseEnrolleeNumber(left).localeCompare(normaliseEnrolleeNumber(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });

    if (byEnrollee !== 0) {
      return byEnrollee;
    }

    return encounterDateKey(left).localeCompare(encounterDateKey(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });

  const duplicateCounts = new Map();
  const duplicateGroups = new Map();
  const enrolleeDates = new Map();
  const multipleVisitGroups = new Map();

  sortedEncounters.forEach((encounter) => {
    const enrolleeKey = normaliseEnrolleeNumber(encounter);
    const dateKey = encounterDateKey(encounter);

    if (!enrolleeKey || !dateKey) {
      return;
    }

    const duplicateKey = `${enrolleeKey}|${dateKey}`;
    duplicateCounts.set(duplicateKey, (duplicateCounts.get(duplicateKey) || 0) + 1);
    duplicateGroups.set(duplicateKey, [...(duplicateGroups.get(duplicateKey) || []), encounter]);

    if (!enrolleeDates.has(enrolleeKey)) {
      enrolleeDates.set(enrolleeKey, new Set());
    }
    enrolleeDates.get(enrolleeKey).add(dateKey);
    multipleVisitGroups.set(enrolleeKey, [...(multipleVisitGroups.get(enrolleeKey) || []), encounter]);
  });

  const duplicateKeys = new Set([...duplicateCounts.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  const multipleVisitKeys = new Set([...enrolleeDates.entries()].filter(([, dates]) => dates.size > 1).map(([key]) => key));

  return sortedEncounters.map((encounter) => ({
    ...encounter,
    reviewMarkers: buildMarkers(encounter, duplicateKeys, multipleVisitKeys, duplicateGroups, multipleVisitGroups),
  }));
};
